import { Box } from "@mui/material"
import { useFocusable } from "@noriginmedia/norigin-spatial-navigation"
import React, { MutableRefObject, useEffect } from "react"
import { useLocation } from "react-router"
import { FacetType, SearchGlyphItem } from "src/generated/graphql"
import { useSmoothNavigate } from "src/hooks/use-smooth-navigate"
import { focusStore } from "src/store/FocusStore"
import { hwScroll, s } from "src/utilities/common"
import { dimensions } from "src/utilities/constants"
import { theme, TI } from "src/utilities/theme"

import { DGText } from "../../../../components/common/DGText"
import { mapAbstractFacetType } from "../../../FacetDetail/FacetDetailScreen"

export const ResultItem = ({
  result,
  scrollRef,
  facetType,
  glyph,
}: {
  result: SearchGlyphItem
  scrollRef: MutableRefObject<HTMLElement | null>
  facetType: FacetType
  glyph: string
}) => {
  const navigate = useSmoothNavigate()
  const addFocus = focusStore((state) => state.addFocus)
  const findMeta = focusStore((state) => state.findMeta)
  const clearMeta = focusStore((state) => state.clearMeta)
  const location = useLocation()

  const focusKey = "FacetBrowseResultItem:" + result.id + result.label

  const onPressAction = () => {
    // DGAPP-6483: go directly to detail page
    const type = mapAbstractFacetType[facetType]
    navigate(`../detail/${type}/${result.id}/0/0/0`)
    //navigate(`facetdetail/${facetType}/${result.id}`)
    // using the store function here to avoid drilling the parent addFocus method
    addFocus({
      route: location.pathname + location.key,
      key: focusKey,
      meta: { glyph, focusKey },
    })
  }
  const { ref, focused, focusSelf } = useFocusable({
    focusKey,
    onFocus: ({ y }, _, query) => {
      if (!(query && query.noFacetScroll))
        hwScroll({ scrollRef, y, height: dimensions.height - s(390) })
    },
    onEnterPress: onPressAction,
  })

  useEffect(() => {
    const meta = findMeta({ route: location.pathname + location.key })
    if (
      meta &&
      typeof meta === "object" &&
      "focusKey" in meta &&
      meta.focusKey === focusKey
    ) {
      clearMeta({ route: location.pathname + location.key })
      focusSelf()
    }
  }, [focusSelf])

  return (
    <Box
      ref={ref}
      aria-label={result.label}
      tabIndex={0}
      onMouseEnter={() => focusSelf({ noFacetScroll: true })}
      onMouseDown={() => onPressAction()}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          mb: s("8px"),
          background: focused ? "white" : undefined,
          pt: s("12px"),
          pb: s("14px"),
          pl: s("40px"),
          pr: s("40px"),
          boxShadow: focused
            ? `${s("0px 30px 112px 0px")} rgba(0, 0, 0, 0.60)`
            : undefined,
          borderRadius: s("4px"),
        }}
      >
        <DGText
          tx={TI.Subheadline02tv}
          sx={{
            fontWeight: focused ? 600 : 400,
            color: focused ? "black" : theme.palette.white[70],
          }}
        >
          {result.label}
        </DGText>
        <DGText
          tx={TI.Subheadline03tv}
          sx={{
            color: focused ? "black" : theme.palette.white[70],
          }}
        >
          {result.count}
        </DGText>
      </Box>
    </Box>
  )
}
